import { create } from 'zustand';
import type { TimUnreadDetails } from './tim-bridge';

export type TimOpenSource =
  | 'manual'
  | 'email'
  | 'apppush'
  | 'toast'
  | 'whatsapp'
  | 'sms';
export type TimUnreadCountsByOrderId = Record<string, number>;

export const parseTimOpenSource = (value?: string | null): TimOpenSource => {
  if (
    value === 'manual' ||
    value === 'email' ||
    value === 'apppush' ||
    value === 'whatsapp' ||
    value === 'sms'
  ) {
    return value;
  }
  return 'manual';
};

export const isTimAutoOpenSource = (source: TimOpenSource) =>
  source !== 'manual' && source !== 'toast';

type OpenTimModalParams = {
  campaignId?: string | number | null;
  orderId?: string | number | null;
  source?: TimOpenSource;
};

type TimModalStore = {
  showTimModal: boolean;
  timUnreadCount: number;
  timUnreadCountsByOrderId: TimUnreadCountsByOrderId;
  currentCampaignId: string;
  currentOrderId: string;
  openSource: TimOpenSource;
  /** 每次打开请求递增，用于把相同 order 的二次点击当作新命令 */
  openSeq: number;
  /** iframe 已完成首次初始化（iframe 已挂载，src 不含 token） */
  timLoaded: boolean;
  /** iframe src（裸 URL，token 通过 SET_STATE AUTH 发送） */
  timUrl: string;
  setShowTimModal: (show: boolean, params?: OpenTimModalParams) => void;
  /** 恢复常驻 iframe 的当前会话，不创建新的 OPEN 导航命令。 */
  resumeTimModal: () => void;
  setTimUnreadCount: (count: number, details?: TimUnreadDetails) => void;
  /** 幂等：首次调用时写入 timUrl 并标记 timLoaded=true */
  setTimReady: (url: string) => void;
};

const toStoreValue = (value?: string | number | null) =>
  value == null ? '' : String(value);

const normalizeUnreadCount = (count: number) => Math.max(0, Number(count) || 0);

const buildOrderUnreadCounts = (details: TimUnreadDetails) => {
  const next: TimUnreadCountsByOrderId = {};
  for (const item of details.items || []) {
    const orderId = toStoreValue(item.orderId);
    const count = normalizeUnreadCount(item.count);
    if (!orderId || count <= 0) continue;
    next[orderId] = (next[orderId] || 0) + count;
  }
  return next;
};

export const getTimUnreadCountByOrderId = (
  counts: TimUnreadCountsByOrderId,
  orderId?: string | number | null
) => {
  const key = toStoreValue(orderId);
  return key ? counts[key] || 0 : 0;
};

export const useTimModalStore = create<TimModalStore>()((set) => ({
  showTimModal: false,
  timUnreadCount: 0,
  timUnreadCountsByOrderId: {},
  currentCampaignId: '',
  currentOrderId: '',
  openSource: 'manual',
  openSeq: 0,
  timLoaded: false,
  timUrl: '',
  setShowTimModal: (show, params) =>
    set((state) => ({
      showTimModal: show,
      currentCampaignId: show ? toStoreValue(params?.campaignId) : '',
      currentOrderId: show ? toStoreValue(params?.orderId) : '',
      openSource: show ? params?.source || 'manual' : state.openSource,
      openSeq: show ? state.openSeq + 1 : state.openSeq
    })),
  resumeTimModal: () => set({ showTimModal: true, openSource: 'manual' }),
  setTimUnreadCount: (count, details) =>
    set({
      timUnreadCount: normalizeUnreadCount(count),
      timUnreadCountsByOrderId:
        details?.scope === 'order_groups' ? buildOrderUnreadCounts(details) : {}
    }),
  setTimReady: (url) =>
    set((state) => (state.timLoaded ? state : { timLoaded: true, timUrl: url }))
}));
